"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle } from "lucide-react";
import SectionTitle from "./section-title";
import { useRTL } from "@/hooks/useRTL";
import { useMediaQuery } from "@/hooks/use-media-query";
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { useTranslation } from "@/hooks/useTranslation";

// Lab features will be defined inside component with translations

const tabContentVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

function DentalLabSection() {
  const { t } = useTranslation('home');
  const { isRTL } = useRTL();
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const labFeatures = [
    {
      id: "3d-scanning",
      title: t('dentalLab.features.scanning.title'),
      description: t('dentalLab.features.scanning.description'),
    },
    {
      id: "cad-cam",
      title: t('dentalLab.features.cadcam.title'),
      description: t('dentalLab.features.cadcam.description'),
    },
    {
      id: "quality-assurance",
      title: t('dentalLab.features.quality.title'),
      description: t('dentalLab.features.quality.description'),
    },
    {
      id: "rapid-turnaround",
      title: t('dentalLab.features.turnaround.title'),
      description: t('dentalLab.features.turnaround.description'),
    },
  ];

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const imageY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const contentX = useTransform(scrollYProgress, [0, 0.5], [isRTL ? 100 : -100, 0]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const staticImageStyle = { y: 0, opacity: 1 };
  const staticContentStyle = { x: 0, opacity: 1 };

  const imageStyle = isClient && !isMobile
    ? { y: imageY, opacity: imageOpacity }
    : staticImageStyle;

  const contentStyle = isClient && !isMobile
    ? { x: contentX, opacity: contentOpacity }
    : staticContentStyle;

  return (
    <section ref={sectionRef} className="py-20 bg-background relative">
      <div className="container mx-auto px-4">
        <SectionTitle>{t('dentalLab.title')}</SectionTitle>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start mt-12">
          <motion.div
            style={contentStyle}
            className="space-y-6"
          >
            <div className="text-lg text-foreground/80 dark:text-slate-200 space-y-4 mb-8">
              <p>
                {t('dentalLab.description.intro')}
              </p>

              <p>
                {t('dentalLab.description.underOneRoof')}
              </p>

              <ul className="list-disc pl-5 space-y-2 text-foreground/70 dark:text-slate-300">
                <li>{t('dentalLab.description.benefits.cadcam')}</li>
                <li>{t('dentalLab.description.benefits.milling')}</li>
                <li>{t('dentalLab.description.benefits.veneers')}</li>
                <li>{t('dentalLab.description.benefits.quality')}</li>
              </ul>

              <p>
                {t('dentalLab.description.result')}
              </p>
            </div>

            <Tabs
              defaultValue="3d-scanning"
              className="w-full"
            >
              <div className="mt-4 md:mt-0">
                <TabsList className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 bg-muted/50 p-2 rounded-lg mb-6 md:mb-8 h-auto">
                  {(isClient && isRTL ? [...labFeatures].reverse() : labFeatures).map((feature) => (
                    <TabsTrigger
                      key={feature.id}
                      value={feature.id}
                      className="text-sm md:text-base w-full h-auto min-h-[56px] flex items-center justify-center py-3 px-3 text-center whitespace-normal data-[state=active]:bg-background data-[state=inactive]:bg-muted/80 hover:bg-muted/90 rounded-md transition-colors rtl:text-right"
                    >
                      <span className="line-clamp-2">{feature.title}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
                <div className="md:mt-8">
                  <AnimatePresence mode="sync">
                    {labFeatures.map((feature) => (
                      <TabsContent key={feature.id} value={feature.id}>
                        <motion.div
                          variants={tabContentVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          layout
                        >
                          <Card className="mt-4 md:mt-10">
                            <CardContent className="pt-6">
                              <motion.h3
                                className="text-xl font-semibold mb-3 text-foreground dark:text-slate-100"
                                layout
                              >
                                {feature.title}
                              </motion.h3>
                              <motion.p className="text-foreground/70 dark:text-slate-300" layout>
                                {feature.description}
                              </motion.p>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </TabsContent>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </Tabs>
          </motion.div>

          <motion.div
            ref={imageRef}
            style={imageStyle}
            className="relative h-[350px] md:h-[600px] mt-6 md:mt-0"
          >
            <OptimizedImage
              src="/images/env/dental-lab.webp"
              alt="Dental Technology"
              fill={true}
              style={{ objectFit: "cover" }}
              className="rounded-lg shadow-xl"
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <motion.div
              className="absolute -bottom-4 -left-4 p-3 text-sm md:text-base md:-bottom-6 md:-left-6 md:p-4 bg-card rounded-lg shadow-lg"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: false }}
              transition={{ type: "spring", stiffness: 200, delay: 0.5 }}
            >
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span className="font-semibold">{t('dentalLab.badge')}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default DentalLabSection;
